<template>
  <div class="pm-view">

    <!-- ─── Saved confirmation banner ─── -->
    <div 
      v-if="$route.query.saved === 'true'"
      class="pm-saved-banner"
    >
      <i class="material-icons pm-info-icon">info</i>
      <span class="pm-banner-text">{{ $t('dash.presenceManagement.location.syncWarning') }}</span>
      <i class="material-icons pm-close-banner" @click="clearSavedQuery">close</i>
    </div>

    <!-- ─── Toolbar ─── -->
    <div class="pm-toolbar">
      <vs-input
        v-model="localSearch"
        @input="onSearchInput"
        @keypress.enter.prevent
        icon-after="true"
        icon="search"
        class="pm-search"
        :placeholder="$t('dash.presenceManagement.search.placeholder')"
      />
      <vs-button color="#6a1f4c" type="filled" class="pm-action-btn" @click="$router.push({ name: 'presence-management-hours' })">
        {{ $t('dash.presenceManagement.actions.bulkHours') }}
      </vs-button>
      <vs-button color="#6a1f4c" type="filled" class="pm-action-btn" @click="$router.push({ name: 'presence-management-special-hours' })">
        {{ $t('dash.presenceManagement.actions.bulkHolidays') }}
      </vs-button>      <div class="pm-count">
        <span class="pm-count-number">{{ displayedCount }}</span>
        <span class="pm-count-label">{{ $t('dash.presenceManagement.count.label') }}</span>
      </div>
    </div>

    <!-- ─── Table ─── -->
    <div class="pm-table">

      <!-- Head row -->
      <div class="pm-row pm-row--head">
        <div class="pm-cell pm-cell--networks pm-cell--sortable" @click="toggleSort('network')">
          {{ $t('dash.presenceManagement.table.columns.networks') }}
          <i class="material-icons pm-sort-icon">{{ sortIcon('network') }}</i>
        </div>
        <div class="pm-cell pm-cell--identity pm-cell--sortable" @click="toggleSort('name')">
          {{ $t('dash.presenceManagement.table.columns.name') }}
          <i class="material-icons pm-sort-icon">{{ sortIcon('name') }}</i>
        </div>
        <div class="pm-cell pm-cell--days">{{ $t('dash.presenceManagement.table.columns.openDays') }}</div>
        <div class="pm-cell pm-cell--completion pm-cell--sortable" @click="toggleSort('completion')">
          {{ $t('dash.presenceManagement.table.columns.completion') }}
          <i class="material-icons pm-sort-icon">{{ sortIcon('completion') }}</i>
        </div>
        <div class="pm-cell pm-cell--marker"></div>
      </div>

      <!-- Loading skeleton rows -->
      <div v-if="loading" class="pm-skeletons">
        <div v-for="n in (selectedPresencePageIds.length || 3)" :key="`skel-init-${n}`" class="pm-row pm-row--data pm-row--skeleton">
          <div class="pm-cell pm-cell--networks">
            <div class="pm-skeleton-box pm-skeleton-icon"></div>
          </div>
          <div class="pm-cell pm-cell--identity">
            <div class="pm-skeleton-box pm-skeleton-avatar"></div>
            <div class="pm-skeleton-meta">
              <div class="pm-skeleton-box pm-skeleton-title"></div>
              <div class="pm-skeleton-box pm-skeleton-sub"></div>
            </div>
          </div>
          <div class="pm-cell pm-cell--days">
            <div class="pm-skeleton-box pm-skeleton-pills"></div>
          </div>
          <div class="pm-cell pm-cell--completion">
            <div class="pm-skeleton-box pm-skeleton-donut"></div>
          </div>
          <div class="pm-cell pm-cell--marker"></div>
        </div>
      </div>

      <!-- Empty state -->
      <p v-else-if="!displayedLocations.length && !isStreaming" class="pm-empty">
        {{ $t('dash.presenceManagement.table.empty') }}
      </p>

      <!-- Data rows -->
      <template v-else>
        <div
          v-for="location in displayedLocations"
          :key="location.id"
          class="pm-row pm-row--data pm-row--clickable"
          @click="$router.push({ name: 'presence-management-location', params: { id: location.id } })"
        >
        
          <!-- Active networks -->
          <div class="pm-cell pm-cell--networks">
            <img
              v-if="location.networks.includes('google')"
              :src="require('../../../assets/img/iconsNetwork/ico-google-2x.png')"
              alt="Google Business Profile"
              class="pm-network-icon"
            />
            <img
              v-else-if="location.networks.includes('facebook')"
              :src="require('../../../assets/img/iconsNetwork/ico-facebook-2x.png')"
              alt="Facebook Page"
              class="pm-network-icon"
            />
            <img
              v-else-if="location.networks.includes('instagram')"
              :src="require('../../../assets/img/iconsNetwork/ico-instagram-2x.png')"
              alt="Instagram Pro"
              class="pm-network-icon"
            />
            <img
              v-else-if="location.networks.includes('apple')"
              :src="require('../../../assets/img/iconsNetwork/ico-apple-2x.png')"
              alt="Apple Maps"
              class="pm-network-icon"
            />
          </div>

          <!-- Identity: thumbnail + name + address -->
          <div class="pm-cell pm-cell--identity">
            <div class="pm-thumb">
              <img :src="location.imageUrl" :alt="location.title || 'établissement'" referrerpolicy="no-referrer" />
            </div>
            <div class="pm-location-meta">
              <p class="pm-location-name">{{ location.title }}</p>
              <p class="pm-location-addr">{{ location.address }}</p>
            </div>
          </div>

          <!-- Open days -->
          <div class="pm-cell pm-cell--days">
            <template v-if="isFacebookWithoutAddress(location)">
              <span
                class="pm-days-no-address-link"
                @click.stop="$router.push({ name: 'presence-management-location', params: { id: location.id }, query: { tab: 'profile' } })"
                :title="$t('dash.presenceManagement.table.clickToFillAddress')"
              >
                <!-- <i class="material-icons pm-no-addr-icon">edit_location</i> -->
                <span>{{ $t('dash.presenceManagement.location.noAddress') }}</span>
              </span>
            </template>
            <template v-else-if="!supportsHours(location)">
              <span
                class="pm-days-na"
                :title="$t('dash.presenceManagement.days.notSupportedTooltip', { network: getNetworkDisplayName(location.network) })"
              >
                {{ $t('dash.presenceManagement.days.notSupported') }}
              </span>
            </template>
            <template v-else>
              <span
                v-for="day in dayPills"
                :key="`${location.id}-${day.key}`"
                :class="['pm-day-pill', { 'pm-day-pill--active': location.openDays.includes(day.value), 'pm-day-pill--special': location.specialDaysThisWeek && location.specialDaysThisWeek.includes(day.value) }]"
                @click.stop="$router.push({ name: 'presence-management-location', params: { id: location.id }, query: { tab: 'specialHours' } })"
                :title="location.specialDaysThisWeek && location.specialDaysThisWeek.includes(day.value) ? $t('dash.presenceManagement.days.specialDayThisWeekTooltip') : null"
              >
                <span 
                  v-if="location.specialDaysThisWeek && location.specialDaysThisWeek.includes(day.value)" 
                  class="pm-day-pill-alert"
                >!</span>
                {{ day.label }}
              </span>
            </template>
          </div>



          <!-- Completion -->
          <div class="pm-cell pm-cell--completion" :title="location.missingCount > 0 ? $t('dash.presenceManagement.table.missingInfo', { count: location.missingCount }) : $t('dash.presenceManagement.table.fullyCompleted')">
            <CompletionDonut :value="location.completionScore" :band="location.completionBand" />
          </div>

          <!-- Right-edge band marker -->
          <div class="pm-cell pm-cell--marker" :class="`pm-marker--${location.completionBand}`"></div>
        </div>

        <!-- Ongoing streaming skeleton rows for pages still loading in background -->
        <div v-if="isStreaming && remainingSkeletonCount > 0" class="pm-skeletons pm-skeletons--streaming">
          <div v-for="n in remainingSkeletonCount" :key="`skel-rem-${n}`" class="pm-row pm-row--data pm-row--skeleton">
            <div class="pm-cell pm-cell--networks">
              <div class="pm-skeleton-box pm-skeleton-icon"></div>
            </div>
            <div class="pm-cell pm-cell--identity">
              <div class="pm-skeleton-box pm-skeleton-avatar"></div>
              <div class="pm-skeleton-meta">
                <div class="pm-skeleton-box pm-skeleton-title"></div>
                <div class="pm-skeleton-box pm-skeleton-sub"></div>
              </div>
            </div>
            <div class="pm-cell pm-cell--days">
              <div class="pm-skeleton-box pm-skeleton-pills"></div>
            </div>
            <div class="pm-cell pm-cell--completion">
              <div class="pm-skeleton-box pm-skeleton-donut"></div>
            </div>
            <div class="pm-cell pm-cell--marker"></div>
          </div>
        </div>
      </template>

    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex'
import CompletionDonut from './components/ui-core/CompletionDonut.vue'

const DAY_PILLS = [
  { key: 'monday', value: 'MONDAY' },
  { key: 'tuesday', value: 'TUESDAY' },
  { key: 'wednesday', value: 'WEDNESDAY' },
  { key: 'thursday', value: 'THURSDAY' },
  { key: 'friday', value: 'FRIDAY' },
  { key: 'saturday', value: 'SATURDAY' },
  { key: 'sunday', value: 'SUNDAY' }
]

const DAY_FALLBACK_LABELS = {
  monday: 'lun',
  tuesday: 'mar',
  wednesday: 'mer',
  thursday: 'jeu',
  friday: 'ven',
  saturday: 'sam',
  sunday: 'dim'
}

export default {
  name: 'presence-management-view',

  components: {
    CompletionDonut
  },

  data() {
    return {
      localSearch: '',
      localLoading: true
    }
  },

  computed: {
    ...mapState('presenceManagement', ['sortBy', 'sortOrder', 'isStreaming', 'totalPagesExpected']),
    ...mapGetters('presenceManagement', ['filteredSortedLocations']),
    ...mapGetters('selectPage', {
      selectedPageIds: 'selectPagePageSelected',
      allPages: 'selectPageAllPages'
    }),

    loading() {
      return this.displayedLocations.length === 0 && (this.isStreaming || this.localLoading)
    },

    remainingSkeletonCount() {
      if (!this.isStreaming) return 0
      const totalExpected = this.selectedPresencePageIds.length || this.totalPagesExpected || 1
      const currentLoaded = this.displayedLocations.length
      const diff = totalExpected - currentLoaded
      return diff > 0 ? diff : 0
    },

    isTesterUserInProd() {
      if (process.env.NODE_ENV !== 'production') return false
      const u = (localStorage.user && localStorage.user !== 'undefined' ? JSON.parse(localStorage.user) : null)
      if (!u) return false
      const isMashup = u.email && u.email.toLowerCase().endsWith('@mashup-web.com')
      if (isMashup) return false
      const testerId = '9e046e84-5b26-4c94-8379-9a88ea278490'
      return u.account === testerId || u.id === testerId
    },

    /**
     * IDs of the currently selected accounts (from SelectPageSection).
     * Supports Google, Facebook, Instagram, and Apple networks.
     */
    selectedPresencePageIds() {
      const allowedNetworks = this.isTesterUserInProd ? ['google', 'facebook'] : ['google', 'facebook', 'apple']
      return this.selectedPageIds.filter(
        id => allowedNetworks.includes(this.allPages[id]?.network) && !this.allPages[id]?.story_config?.onlyConciergerie
      )
    },

    displayedLocations() {
      if (!this.selectedPresencePageIds.length) return []
      return this.filteredSortedLocations.filter(location => {
        // Safe fallback for mock locations that do not have localPageId
        if (!location.localPageId) return true
        return this.selectedPresencePageIds.includes(location.localPageId)
      })
    },

    displayedCount() {
      return this.displayedLocations.length
    },

    dayPills() {
      return DAY_PILLS.map(day => {
        const key = `dash.presenceManagement.days.short.${day.key}`
        const translated = this.$t(key)
        return {
          ...day,
          label: translated === key ? DAY_FALLBACK_LABELS[day.key] : translated
        }
      })
    }
  },

  watch: {
    displayedLocations: {
      immediate: true,
      handler(val) {
        if (val && val.length > 0) {
          this.localLoading = false
        }
      }
    }
  },

  async created() {
    const hasCachedLocations = this.$store.state.presenceManagement.locations && this.$store.state.presenceManagement.locations.length > 0
    this.localLoading = !hasCachedLocations
    try {
      await this.initMockLocations()
    } finally {
      this.localLoading = false
    }
  },

  methods: {
    ...mapActions('presenceManagement', ['initMockLocations', 'updateSearchTerm', 'toggleSort']),

    onSearchInput() {
      this.updateSearchTerm(this.localSearch)
    },

    sortIcon(column) {
      if (this.sortBy !== column) return 'unfold_more'
      return this.sortOrder === 'asc' ? 'keyboard_arrow_up' : 'keyboard_arrow_down'
    },

    clearSavedQuery() {
      this.$router.replace({ query: {} })
    },

    supportsHours(location) {
      const net = String(location.network || (Array.isArray(location.networks) && location.networks[0]) || 'google').toLowerCase()
      return ['google', 'facebook', 'apple'].includes(net)
    },

    isFacebookWithoutAddress(location) {
      const net = String(location.network || (Array.isArray(location.networks) && location.networks[0]) || 'google').toLowerCase()
      if (net !== 'facebook') return false
      const addr = location.rawAddress || location.storefrontAddress || location.address
      if (!addr) return true
      if (typeof addr === 'string') {
        const clean = addr.trim().toLowerCase()
        return !clean || clean === 'sans adresse' || clean === 'établissement sans adresse'
      }
      const hasStreet = addr.addressLines && addr.addressLines.length > 0 && !!addr.addressLines[0].trim()
      const hasCity = addr.locality && !!addr.locality.trim()
      return !(hasStreet || hasCity)
    },

    getNetworkDisplayName(network) {
      const net = String(network || 'google').toLowerCase()
      if (net === 'instagram') return 'Instagram'
      if (net === 'facebook') return 'Facebook'
      if (net === 'apple') return 'Apple Maps'
      if (net === 'google') return 'Google'
      return network
    }
  }
}
</script>

<style scoped>
/* ─── Root ─── */
.pm-view {
  width: 100%;
}

/* ─── Saved confirmation banner ─── */
.pm-saved-banner {
  display: flex;
  align-items: center;
  gap: 12px;
  background-color: #f3edf7;
  border-left: 4px solid #6a1f4c;
  color: #6a1f4c;
  padding: 12px 16px;
  border-radius: 8px;
  margin-bottom: 16px;
  font-size: 13.5px;
  font-weight: 600;
  box-shadow: 0 2px 8px rgba(106, 31, 76, 0.05);
}

.pm-info-icon {
  color: #6a1f4c;
  font-size: 20px;
  display: inline-flex;
}

.pm-banner-text {
  flex: 1;
  line-height: 1.4;
}

.pm-close-banner {
  cursor: pointer;
  font-size: 18px;
  opacity: 0.7;
  transition: opacity 0.2s;
}

.pm-close-banner:hover {
  opacity: 1;
}

/* ─── Toolbar ─── */
.pm-toolbar {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 16px;
}

.pm-search {
  width: 360px !important;
}

::v-deep .pm-search .vs-con-input {
  border: 1.6px solid #f1edf5;
  border-radius: 5.6px;
  height: 35.2px;
  background: #fff;
}

::v-deep .pm-search .vs-inputx {
  height: 100%;
  border: 0 !important;
  background: transparent !important;
  color: #555e75;
  font-size: 13.6px;
  font-weight: 500;
}

::v-deep .pm-search .vs-icon {
  color: #ab6e93 !important;
  border-left: 0 !important;
  border-right: 0 !important;
  width: 32px;
  font-size: 21px !important;
  display: flex;
  align-items: center;
  justify-content: center;
}

::v-deep .pm-search .vs-icon::before,
::v-deep .pm-search .vs-icon::after {
  border: 0 !important;
  box-shadow: none !important;
}

.pm-action-btn {
  font-weight: 700;
  font-size: 13px;
  white-space: nowrap;
  line-height: 1.2;
}

.pm-action-btn ::v-deep span {
  text-transform: none !important;
}

.pm-count {
  margin-left: auto;
  display: flex;
  align-items: baseline;
  gap: 7px;
  white-space: nowrap;
}

.pm-count-number {
  font-size: 26px;
  font-weight: 800;
  color: #2f1937;
  line-height: 1;
}

.pm-count-label {
  font-size: 14px;
  font-weight: 700;
  color: #2f1937;
}

/* ─── Table shell ─── */
.pm-table {
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 12px rgba(46, 25, 55, 0.07);
}

/* ─── Row grid (head + data share same columns) ─── */
.pm-row {
  display: grid;
  /* grid-template-columns: minmax(190px, 2fr) 240px 200px 150px 6px; */
  grid-template-columns: 90px 2fr 300px 200px 6px;
  align-items: stretch;
}

/* ─── Head row ─── */
.pm-row--head {
  background: #6a1f4c;
  min-height: 44px;
}

.pm-row--head .pm-cell {
  color: #fff;
  font-size: 13px;
  font-weight: 700;
  padding: 0 14px;
  display: flex;
  align-items: center;
  white-space: nowrap;
}

.pm-row--head .pm-cell--marker {
  padding: 0;
}

.pm-cell--sortable {
  cursor: pointer;
  gap: 4px;
  user-select: none;
}

.pm-sort-icon {
  font-size: 16px;
}

/* ─── Data rows (card style on #f0ecf4 page bg) ─── */
.pm-row--data {
  background: #fff;
  border-top: 2px solid #f3edf7;
  border-bottom: 2px solid #f3edf7;
  min-height: 76px;
}

.pm-row--clickable {
  cursor: pointer;
  transition: background-color 0.15s;
}

.pm-row--clickable:hover {
  background-color: #fdf9fd;
}

.pm-row--data:last-child {
  border-bottom: 0;
}

.pm-row--data .pm-cell {
  padding: 10px 14px;
  display: flex;
  align-items: center;
}

.pm-row--data .pm-cell--marker {
  padding: 0;
}

/* ─── Identity cell ─── */
.pm-cell--identity {
  gap: 12px;
  min-width: 0;
}

.pm-thumb {
  width: 46px;
  height: 46px;
  border-radius: 6px;
  overflow: hidden;
  flex: 0 0 46px;
  background: #f3edf7;
}

.pm-thumb img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.pm-location-meta {
  min-width: 0;
}

.pm-location-name {
  margin: 0;
  font-size: 14px;
  font-weight: 700;
  color: #1a1325;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.pm-location-addr {
  margin: 3px 0 0;
  font-size: 12px;
  color: #8c7d96;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* ─── Days cell ─── */
.pm-cell--days {
  justify-content: center;
  gap: 5px;
  flex-wrap: wrap;
  cursor:default;
}

.pm-day-pill {
  position: relative;
  width: 26px;
  height: 26px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 9px;
  font-weight: 700;
  text-transform: lowercase;
  background: #e8e0ee;
  color: #5a4d64;
  flex: 0 0 auto;
  transition: transform 0.15s, box-shadow 0.15s;
}

.pm-day-pill--active {
  background: #B64985;
  color: #fff;
}

.pm-day-pill--special {
  cursor: pointer;
}

.pm-day-pill--special:hover {
  transform: scale(1.15);
}

.pm-day-pill-alert {
  position: absolute;
  top: -4px;
  right: -4px;
  width: 14px;
  height: 14px;
  border-radius: 50%;
  background: #f59e0b;
  color: #fff;
  font-size: 10px;
  font-weight: 900;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 1px 4px rgba(0,0,0,0.3);
  border: 1.5px solid #fff;
  line-height: 1;
}

.pm-days-na {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  color: #5a4d64;
  border-radius: 6px;
  padding: 4px 10px;
  font-size: 11px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s;
}

.pm-days-na:hover {
  background: #e9e2ee;
  transform: translateY(-1px);
}

.pm-days-no-address-link {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  color: #5a4d64;
  border-radius: 6px;
  padding: 4px 10px;
  font-size: 11px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s;
}

.pm-days-no-address-link:hover {
  background: #e9e2ee;
  transform: translateY(-1px);

  .pm-no-addr-icon {
    color: #fff;
  }
}

.pm-no-addr-icon {
  font-size: 14px;
  color: #5a4d64;
}

/* ─── Networks cell ─── */
.pm-cell--networks {
  justify-content: center;
}

.pm-network-icon {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  object-fit: cover;
}

/* ─── Completion cell ─── */
.pm-cell--completion {
  justify-content: center;
}

/* ─── Right-edge band marker ─── */
.pm-cell--marker {
  padding: 0;
}

.pm-marker--full {
  background: #6a1f4c;
}

.pm-marker--medium {
  background: #B64985;
}

.pm-marker--low {
  background: #d9b3ca;
}

/* ─── Empty state ─── */
.pm-empty {
  margin: 0;
  padding: 28px;
  text-align: center;
  color: #6d5c78;
  font-weight: 600;
  background: #fff;
}

/* ─── Responsive ─── */
@media (max-width: 1280px) {
  .pm-table {
    overflow-x: auto;
  }

  .pm-row {
    min-width: 980px;
    grid-template-columns: minmax(2fr) 150px 150px 150px 6px;
  }

  .pm-toolbar {
    flex-wrap: wrap;
    gap: 8px;
  }
  
  .pm-search {
    width: 100% !important;
    min-width: 280px;
    flex: 1 1 100%;
  }
  
  .pm-action-btn {
    flex: 0 0 auto;
  }
  
  .pm-count {
    margin-left: auto;
  }
}

@media (max-width: 1024px) {
  .pm-count {
    /* margin-left: 0; */
  }
}

@media (max-width: 960px) {
  .pm-row {
    min-width: 760px;
    grid-template-columns: minmax(200px,2fr) 150px 150px 150px 6px;
  }

  .pm-count {
    display: none;
  }
}

/* ─── Skeleton Loader ─── */
.pm-row--skeleton {
  pointer-events: none;
  background: #ffffff;
}

.pm-skeleton-box {
  background: linear-gradient(90deg, #f0f0f0 25%, #e8e8e8 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: pmShimmer 1.5s infinite;
  border-radius: 4px;
}

@keyframes pmShimmer {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

.pm-skeleton-icon {
  width: 24px;
  height: 24px;
  border-radius: 50%;
}

.pm-skeleton-avatar {
  width: 42px;
  height: 42px;
  border-radius: 8px;
  flex-shrink: 0;
}

.pm-skeleton-meta {
  display: flex;
  flex-direction: column;
  gap: 6px;
  flex: 1;
}

.pm-skeleton-title {
  width: 140px;
  height: 14px;
}

.pm-skeleton-sub {
  width: 200px;
  height: 11px;
}

.pm-skeleton-pills {
  width: 180px;
  height: 22px;
  border-radius: 11px;
}

.pm-skeleton-donut {
  width: 38px;
  height: 38px;
  border-radius: 50%;
}
</style>
